import { useCallback } from "react";
import { reportBrowserError } from "../services/error-reporting/browser-error-reporter.service";
import { useRouteTracker } from "./use-route-tracker.hook";

export type UseLogErrorReturn = {
  logError: (error: Error | string, additionalData?: Record<string, unknown>) => void;
};

/**
 * Purpose: stable, non-debounced entry point React error boundaries and
 * route error elements use to report a failure. Dedup/rate-bounding is
 * delegated entirely to the shared `ReportGuard` inside `reportBrowserError`
 * — this hook no longer maintains its own debounce (which recreated on every
 * render and silently dropped distinct errors seen within the window) or a
 * blanket ignore-list of message substrings (which hid unexpected failures
 * instead of just rate-bounding them).
 *
 * @example
 * const { logError } = useLogError();
 * logError(error, { componentStack: errorInfo.componentStack });
 */
export function useLogError(): UseLogErrorReturn {
  const { getNavigationHistory } = useRouteTracker();

  const logError = useCallback(
    (error: Error | string, additionalData?: Record<string, unknown>) => {
      if (typeof window === "undefined") return;

      const message = typeof error === "string" ? error : error.message;
      const stack = typeof error === "string" ? undefined : error.stack;

      reportBrowserError({
        message,
        stack,
        source: "react-boundary",
        additionalData: {
          ...additionalData,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          screen: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          referer: document.referrer || null,
          navigationHistory: getNavigationHistory()
            .map((entry) => entry.path)
            .join(" > "),
        },
      }).catch(() => undefined);
    },
    [getNavigationHistory],
  );

  return {
    logError,
  };
}
